// Shared low-level CSV mechanics -- a single-pass RFC 4180 tokenizer used
// by the admin panels' bulk-CSV flows (UserManagementPanel.js,
// ClassSectionsPanel.js). Scans the raw text as one character stream (not
// line-then-comma, which can't correctly handle quoting) so a quoted field
// ("...") may contain commas, embedded newlines (LF or CRLF), and an
// escaped "" decodes to one literal ". An *unquoted* comma stays a plain
// delimiter -- quoting is opt-in, so an unquoted comma inside a value
// still misaligns columns; both callers guard this with a
// `values.length !== headers.length` check. Schema-specific field
// extraction (which columns map to which properties, per-row validation)
// stays in each caller.

// Parses raw CSV text into { headers, dataRows }. headers is the first
// row's fields, lowercased and trimmed. dataRows is every other row as an
// already-tokenized array of field strings. Blank lines (no delimiter seen
// and the single field is empty/whitespace-only) are skipped; a row of
// only empty fields separated by real commas (e.g. ",,") is not blank and
// is kept.
export interface ParsedCsv {
  headers: string[];
  dataRows: string[][];
}

export function parseCsv(csvText?: string | null): ParsedCsv {
  const text = csvText || '';
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let fieldWasQuoted = false;
  let afterQuoteClose = false;
  let rowSawDelimiter = false;
  let i = 0;

  const endField = (): void => {
    row.push(fieldWasQuoted ? field : field.trim());
    field = '';
    fieldWasQuoted = false;
    afterQuoteClose = false;
  };

  const endRow = (): void => {
    const isBlankRow =
      !rowSawDelimiter && !fieldWasQuoted && field.trim() === '';
    endField();
    if (!isBlankRow) rows.push(row);
    row = [];
    rowSawDelimiter = false;
  };

  while (i < text.length) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          afterQuoteClose = true;
          i += 1;
        }
      } else {
        field += char;
        i += 1;
      }
      continue;
    }

    if (char === '"' && !fieldWasQuoted && field.trim() === '') {
      field = '';
      fieldWasQuoted = true;
      inQuotes = true;
      i += 1;
      continue;
    }

    // Discard whitespace between a field's closing quote and the next
    // delimiter/newline -- it's "outside the quotes" per RFC 4180.
    if (afterQuoteClose && (char === ' ' || char === '\t')) {
      i += 1;
      continue;
    }

    if (char === ',') {
      rowSawDelimiter = true;
      endField();
      i += 1;
      continue;
    }

    if (char === '\r' && text[i + 1] === '\n') {
      endRow();
      i += 2;
      continue;
    }

    // A lone CR (not part of a CRLF, handled above) is an old-style Mac
    // row terminator -- treat it the same as LF rather than folding it
    // into the field content.
    if (char === '\r') {
      endRow();
      i += 1;
      continue;
    }

    if (char === '\n') {
      endRow();
      i += 1;
      continue;
    }

    field += char;
    i += 1;
  }
  if (field !== '' || fieldWasQuoted || row.length > 0) endRow();

  const headers: string[] =
    rows.length > 0 ? rows[0].map((h) => h.toLowerCase()) : [];
  const dataRows = rows.slice(1);
  return { headers, dataRows };
}

// Quotes a CSV field per RFC 4180 (only when it actually contains a comma,
// quote, or newline); doubles any embedded quotes. Shared by the admin
// panels' CSV export/sample-download flows (ClassSectionsPanel.js,
// PassHistoryView.js).
export function escapeCsv(value: unknown): string {
  const str = String(value ?? '');
  if (/["\n,]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Reads a File as text via FileReader, wrapped in a Promise. Shared by the
// admin panels' CSV file-upload handlers (ClassSectionsPanel.js,
// ScheduleConfigPanel.js, UserManagementPanel.js) to avoid duplicating the
// FileReader boilerplate and error copy.
export function readFileAsText(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) =>
      resolve((event.target as FileReader).result as string);
    reader.onerror = () =>
      reject(
        new Error(
          'Error reading CSV file. Please ensure the file is not corrupted and try again.'
        )
      );
    reader.readAsText(file);
  });
}
